import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { errText, platformApi, type PlatformSettings } from "@/admin/api";
import { NativeSelect } from "@/admin/components";

type BoolKey = {
  [K in keyof PlatformSettings]: PlatformSettings[K] extends boolean ? K : never;
}[keyof PlatformSettings];

const SECTIONS: { title: string; note: string; rows: { key: BoolKey; label: string; help: string }[] }[] = [
  {
    title: "Sign-up",
    note: "",
    rows: [
      {
        key: "public_signup_enabled",
        label: "Public sign-up",
        help: "Off = invite-only: the registration page is hidden and new tenants are added under Tenants.",
      },
    ],
  },
  {
    title: "Workspaces",
    note: "",
    rows: [
      {
        key: "multi_workspace_enabled",
        label: "Multi-workspace login",
        help: "One login can belong to several workspaces and switch between them. Tenant admins can invite an existing user.",
      },
    ],
  },
  {
    title: "Suspended tenants",
    note: "What a suspended tenant is blocked from.",
    rows: [
      { key: "block_calls_when_suspended", label: "Calls", help: "Inbound, outbound and test calls." },
      { key: "block_agent_management_when_suspended", label: "Agent management", help: "Creating, editing and deploying agents." },
      { key: "block_phone_numbers_when_suspended", label: "Phone numbers", help: "Buying and provisioning numbers." },
      { key: "block_webhooks_when_suspended", label: "Webhooks", help: "Delivering outbound webhooks." },
      {
        key: "block_post_call_processing_when_suspended",
        label: "Post-call processing",
        help: "Summaries, sentiment, lead scoring and follow-ups after a call.",
      },
    ],
  },
];

export default function AdminSettingsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin", "settings"], queryFn: platformApi.settings });
  const [alertDollars, setAlertDollars] = useState("");
  const [pipeline, setPipeline] = useState("v3");
  useEffect(() => {
    if (q.data) {
      setAlertDollars((q.data.low_credit_alert_cents / 100).toFixed(2));
      setPipeline(q.data.default_pipeline_version);
    }
  }, [q.data]);

  const save = useMutation({
    mutationFn: platformApi.updateSettings,
    onSuccess: (s) => {
      qc.setQueryData(["admin", "settings"], s);
      toast.success("Saved.");
    },
    onError: (e) => toast.error(errText(e, "Couldn't save.")),
  });

  if (q.isLoading || !q.data) return <Skeleton className="h-64 w-full" />;
  const s = q.data;
  const alertCents = Math.round(Number(alertDollars) * 100);
  const otherDirty = alertCents !== s.low_credit_alert_cents || pipeline !== s.default_pipeline_version;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Platform settings</h1>
        <p className="text-sm text-muted-foreground">Settings that apply to the whole platform. Switches save straight away.</p>
      </div>

      {SECTIONS.map((sec) => (
        <Card key={sec.title}>
          <CardHeader>
            <CardTitle className="text-base">{sec.title}</CardTitle>
            {sec.note && <p className="text-sm text-muted-foreground">{sec.note}</p>}
          </CardHeader>
          <CardContent className="divide-y">
            {sec.rows.map((r) => (
              <div key={r.key} className="flex items-center justify-between gap-4 py-3">
                <div>
                  <div className="text-sm font-medium">{r.label}</div>
                  <div className="text-xs text-muted-foreground">{r.help}</div>
                </div>
                <Switch
                  checked={s[r.key]}
                  disabled={save.isPending}
                  onCheckedChange={(v) => save.mutate({ [r.key]: v })}
                />
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Credit and agents</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <div className="text-sm font-medium">Low-credit alert (USD)</div>
              <Input type="number" step="0.01" min={0} value={alertDollars} onChange={(e) => setAlertDollars(e.target.value)} />
              <p className="text-xs text-muted-foreground">
                Owners and admins are alerted when their prepaid credit drops below this, and again when it reaches zero and
                calls stop.
              </p>
            </div>
            <div className="space-y-1.5">
              <div className="text-sm font-medium">Default voice pipeline</div>
              <NativeSelect value={pipeline} onChange={setPipeline}>
                <option value="v3">v3 — Low-latency</option>
                <option value="v4">v4 — Flux turn detection</option>
              </NativeSelect>
              <p className="text-xs text-muted-foreground">The pipeline every new agent starts on. Existing agents keep theirs.</p>
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              disabled={!otherDirty || save.isPending || !Number.isFinite(alertCents) || alertCents < 0}
              onClick={() => save.mutate({ low_credit_alert_cents: alertCents, default_pipeline_version: pipeline })}
            >
              Save
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
